import { useState, useEffect } from 'react';
import { CreditCard, DollarSign, BadgeCheck, Sparkles, Rocket, Shield, Gift, Copy, Check, Coins } from 'lucide-react';
import { FaBitcoin, FaEthereum, FaCreditCard, FaApplePay, FaGooglePay, FaPaypal, FaDollarSign } from 'react-icons/fa';
import { SiTether, SiBinance } from 'react-icons/si';

export default function PresaleInfo() {
  const [interesTokens, setInteresTokens] = useState(200000);
  const [usdtAmount, setUsdtAmount] = useState(4300);
  const [soldTokens] = useState(3500000);
  const [totalTokens] = useState(10000000);
  
  // Updates the USDT price based on tokens
  useEffect(() => {
    // Price per token: $0.004
    const tokenPrice = 0.004;
    setUsdtAmount(Number((interesTokens * tokenPrice).toFixed(2)));
  }, [interesTokens]);
  
  const handleTokenChange = (value) => {
    // Validate that it's a positive number
    if (value >= 0) {
      setInteresTokens(value);
    }
  };
  
  const soldPercentage = (soldTokens / totalTokens) * 100;
  
  return (
    <section id="presale" className="py-8 relative">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-950/2 to-transparent pointer-events-none"></div>
      <div className="absolute -bottom-20 right-0 w-80 h-80 bg-purple-900/5 rounded-full blur-[100px]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-4">
          <h2 className="text-4xl font-bold mb-4 gradient-heading" data-i18n="presale.title">Presale Information</h2>
          <p className="text-xl card-text max-w-3xl mx-auto" data-i18n="presale.description">
            Secure your QuokkaX tokens at the best price before the official launch
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 md:gap-10">
          {/* Left Column - Presale Information */}
          <div className="lg:col-span-7">
            <div className="glass-card p-4 sm:p-6 md:p-8 hover-glow border border-blue-500/10 h-full flex flex-col justify-between">
              {/* Available Tokens */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <h3 className="card-title flex items-center">
                    <Rocket className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mr-2 text-primary" />
                    <span className="gradient-heading text-base sm:text-lg md:text-xl" data-i18n="presale.available_tokens">Available Tokens</span>
                  </h3>
                  <div className="glass-card py-1 px-2 sm:px-3 rounded-full border border-primary/30 card-text text-xs sm:text-sm">
                    <span data-i18n="presale.rate">Rate:</span> <span className="text-primary">35%</span>
                  </div>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="card-text text-sm sm:text-base" data-i18n="presale.tokens_available">Available Tokens</span>
                    <span className="card-subtitle text-sm sm:text-base">{totalTokens.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="card-text text-sm sm:text-base" data-i18n="presale.tokens_sold">Tokens Sold</span>
                    <span className="card-subtitle text-sm sm:text-base">{soldTokens.toLocaleString()}</span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mt-2">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="card-text" data-i18n="presale.sale_progress">Sale Progress</span>
                      <span className="text-primary font-medium">{soldPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 sm:h-2.5 bg-theme-card/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-primary"
                        style={{ width: `${soldPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Price info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                    <div className="glass-card p-3 sm:p-4 text-center">
                      <p className="card-text text-sm mb-1" data-i18n="presale.current_price">Current Price</p>
                      <p className="text-lg sm:text-xl font-bold text-primary">1 QOKX = $0.004</p>
                    </div>
                    <div className="glass-card p-3 sm:p-4 text-center">
                      <p className="card-text text-sm mb-1" data-i18n="presale.next_price">Next Price</p>
                      <p className="text-lg sm:text-xl font-bold text-warning">1 QOKX = $0.008</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Purchase Calculator */}
              <div className="mt-6 sm:mt-8">
                <div className="glass-card p-4 sm:p-6">
                  <div className="mb-4">
                    <label htmlFor="interestAmount" className="block mb-2 text-sm card-text" data-i18n="presale.tokens_to_purchase">
                      Number of tokens to purchase
                    </label>
                    <input
                      id="interestAmount"
                      type="number"
                      value={interesTokens}
                      onChange={(e) => handleTokenChange(Number(e.target.value))}
                      className="w-full glass-card card-text border border-theme-text/20 rounded-lg px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:border-primary text-sm sm:text-base"
                    />
                  </div>
                
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
                    <button
                      onClick={() => {}}
                      className="gradient-button py-3 sm:py-3 text-white font-medium flex items-center justify-center gap-2 hover:shadow-[0_0_15px_rgba(59,130,246,0.4)] min-w-[100px] sm:min-w-[120px] px-3 sm:px-4 text-sm sm:text-base"
                    >
                      <CreditCard className="w-4 h-4 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                      <span data-i18n="presale.buy_now">Buy Now</span>
                    </button>
                    <div className="glass-card flex items-center justify-center rounded-lg py-3 sm:py-3 px-3 sm:px-4">
                      <span className="card-text mr-2 text-sm sm:text-base" data-i18n="presale.total">Total:</span>
                      <span className="text-lg sm:text-xl font-bold gradient-text">${usdtAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Payment Methods */}
              <div className="mt-3 sm:mt-4">
                <h4 className="mb-3 sm:mb-4 gradient-heading inline-block pb-1 border-b-2 border-primary/30 text-base sm:text-lg" data-i18n="presale.payment_methods">
                  Payment Methods
                </h4>
                
                {/* First row of payment methods */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3">
                  {/* USDT */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#26A17B]/40 hover:border-[#26A17B] transition-all flex items-center justify-center cursor-pointer">
                    <SiTether size={18} color="#26A17B" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">USDT</span>
                  </div>
                  
                  {/* BNB */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#F3BA2F]/40 hover:border-[#F3BA2F] transition-all flex items-center justify-center cursor-pointer">
                    <SiBinance size={18} color="#F3BA2F" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">BNB</span>
                  </div>
                  
                  {/* ETH */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#627EEA]/40 hover:border-[#627EEA] transition-all flex items-center justify-center cursor-pointer">
                    <FaEthereum size={18} color="#627EEA" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">ETH</span>
                  </div>
                  
                  {/* BTC */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#F7931A]/40 hover:border-[#F7931A] transition-all flex items-center justify-center cursor-pointer">
                    <FaBitcoin size={18} color="#F7931A" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">BTC</span>
                  </div>
                </div>
                
                {/* Second row of payment methods */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                  {/* PayPal */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#0070ba]/40 hover:border-[#0070ba] transition-all flex items-center justify-center cursor-pointer">
                    <FaPaypal size={18} color="#0070ba" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">PayPal</span>
                  </div>
                  
                  {/* Apple Pay */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#ffffff]/40 hover:border-[#ffffff] transition-all flex items-center justify-center cursor-pointer">
                    <FaApplePay size={20} color="#ffffff" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">Apple Pay</span>
                  </div>
                  
                  {/* Google Pay */}
                  <div className="glass-card hover:bg-theme-card/80 py-2 px-2 sm:px-3 rounded-lg border border-[#4285F4]/40 hover:border-[#4285F4] transition-all flex items-center justify-center cursor-pointer col-span-2 sm:col-span-1">
                    <FaGooglePay size={20} color="#4285F4" className="mr-1 sm:mr-2" />
                    <span className="font-medium text-sm">Google Pay</span>
                  </div>
                </div>
                
                {/* Contract Address */}
                <div className="mt-3 sm:mt-4">
                  <button className="w-full glass-card hover:bg-theme-card/90 border border-blue-500/30 card-text py-3 sm:py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:border-blue-500/50 text-sm sm:text-base">
                    <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <span data-i18n="presale.copy_contract">Copy Contract Address</span>
                  </button>
                </div>
                
                <div className="text-sm card-text mt-3 sm:mt-4 text-center" data-i18n="presale.fee_note">
                  Includes 2% buy tax and 2% sell tax for liquidity, development and ecosystem
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div className="lg:col-span-5">
            <div className="flex flex-col space-y-6 sm:space-y-8 h-full">
              {/* QuokkaX Logo & Buttons */}
              <div className="glass-card p-4 sm:p-6 md:p-8 hover-glow border border-blue-500/10 h-full">
                <div className="text-center mb-4 sm:mb-6">
                  <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold gradient-heading mb-2">QuokkaX</h3>
                </div>
                
                <div className="text-center card-text">
                  <p className="line-clamp-3 text-sm sm:text-base" data-i18n="presale.quokkax_description">The crypto revolution has begun. Participate in the exclusive presale with the available payment methods.</p>
                </div>
              </div>
              
              {/* Benefits */}
              <div className="glass-card p-4 sm:p-6 md:p-8 hover-glow border border-blue-500/10">
                <h4 className="mb-4 sm:mb-6 gradient-heading inline-block border-b-2 border-primary/30 text-base sm:text-lg" data-i18n="presale.benefits_title">Presale Benefits</h4>
                
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.benefit1">Exclusive low price</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.benefit2">Purchase volume bonuses</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.benefit3">Access to staking and NFTs</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.benefit4">Entry to minigame</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.benefit5">Holder raffles</span>
                  </li>
                </ul>
              </div>
              
              {/* Security Block */}
              <div className="glass-card p-4 sm:p-6 md:p-8 hover-glow border border-blue-500/10">
                <h4 className="mb-4 sm:mb-6 gradient-heading inline-block border-b-2 border-primary/30 text-base sm:text-lg" data-i18n="presale.security_title">Security</h4>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.security1">Verified contract</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.security2">CertiK audit</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.security3">KYC with Assure DeFi</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="text-primary p-1 sm:p-1.5 bg-primary/5 rounded-full flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="card-text line-clamp-2 text-sm" data-i18n="presale.security4">Locked liquidity</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 